import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "@components/ui/Spinner";
import Navbar from "@components/layout/Navbar";
import LeadInfoHeader from "@components/sms/LeadInfoHeader";
import ChatThread from "@components/sms/ChatThread";
import ChatInput from "@components/sms/ChatInput";
import { getSmsLead, sendSmsMessage } from "@services/smsLeads";

export default function SmsLeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLead = useCallback(async () => {
    try {
      const lead = await getSmsLead(leadId);
      setLead(lead);
      setMessages(lead.messages || []);
    } catch (err) {
      console.error("Failed to fetch lead:", err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const handleSend = async (body) => {
    const newMsg = await sendSmsMessage(leadId, body);
    setMessages((prev) => [...prev, newMsg]);
  };

  const isSendDisabled = lead && ["DO_NOT_SEND", "BLOCKED"].includes(lead.status);
  const disabledReason =
    lead?.status === "DO_NOT_SEND"
      ? "This contact sent STOP. Sending is permanently disabled (TCPA compliance)."
      : lead?.status === "BLOCKED"
      ? "This contact is blocked. Sending is disabled."
      : "";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar showTabs={false} showFieldSearch={false} />
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar showTabs={false} showFieldSearch={false} />
        <div className="text-center py-12 text-gray-500">Lead not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar showTabs={false} showFieldSearch={false} />

      {/* Back button */}
      <div className="max-w-4xl mx-auto w-full px-4 pt-4">
        <button
          onClick={() => navigate("/sms-leads")}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to SMS Leads
        </button>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col bg-white rounded-lg shadow mt-2 mb-6 mx-4 overflow-hidden"
           style={{ maxHeight: "calc(100vh - 140px)" }}>
        <LeadInfoHeader lead={lead} onUpdate={fetchLead} />
        <ChatThread messages={messages} />
        <ChatInput
          onSend={handleSend}
          disabled={isSendDisabled}
          disabledReason={disabledReason}
        />
      </div>
    </div>
  );
}
